/**
 * Step 2 — Greenfield Options.
 * Vendor-conditional toggles that drive the generator's factory-cleanup blocks.
 */
import React from "react";
import { motion } from "framer-motion";
import { ShieldOff, Trash2, Network, Info } from "lucide-react";
import { useWizardStore } from "../state/wizardStore";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";

const Toggle = ({ on, onChange, disabled, testId }) => (
    <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={disabled}
        onClick={() => !disabled && onChange(!on)}
        data-testid={testId}
        className={[
            "relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-300",
            on ? "bg-accent-cyan shadow-glow-cyan" : "bg-white/[0.08]",
            disabled && "opacity-50 cursor-not-allowed",
        ].join(" ")}
    >
        <span
            className={[
                "inline-block h-5 w-5 rounded-full bg-white shadow-md transform transition-transform duration-300",
                on ? "translate-x-6" : "translate-x-1",
            ].join(" ")}
        />
    </button>
);

const ToggleRow = ({ icon: Icon, title, description, why, on, onChange, testId, defaultOn, disabled, warningWhenOff }) => (
    <div
        className={[
            "rounded-xl border bg-surface/60 backdrop-blur-md p-5 transition-colors duration-200",
            on ? "border-white/[0.08]" : "border-accent-amber/30",
        ].join(" ")}
        data-testid={`greenfield-row-${testId}`}
    >
        <div className="flex items-start gap-4">
            <span className={[
                "inline-flex h-9 w-9 items-center justify-center rounded-lg border shrink-0",
                on ? "bg-accent-cyan/10 border-accent-cyan/30 text-accent-cyan" : "bg-accent-amber/10 border-accent-amber/30 text-accent-amber",
            ].join(" ")}>
                <Icon className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                    <h3 className="font-display font-semibold text-[15px] text-text-primary flex items-center gap-2">
                        {title}
                        <TooltipProvider delayDuration={250}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Info className="w-3.5 h-3.5 text-text-dim hover:text-accent-cyan cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-xs bg-surface border-white/[0.08] text-[12px]">
                                    {why}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </h3>
                    <Toggle on={on} onChange={onChange} disabled={disabled} testId={`${testId}-toggle`} />
                </div>
                <p className="mt-1.5 text-[12.5px] text-text-muted leading-relaxed">{description}</p>
                {!on && warningWhenOff && (
                    <p className="mt-2 text-[10.5px] font-mono text-accent-amber flex items-center gap-1.5">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent-amber" />
                        {warningWhenOff}
                    </p>
                )}
                {defaultOn && on && (
                    <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.22em] text-accent-mint">
                        Recommended default
                    </p>
                )}
            </div>
        </div>
    </div>
);

export const Step2Greenfield = ({ readOnly = false }) => {
    const {
        vendor,
        destroy_switch, remove_dhcp, disable_fortilink,
        updateStep2,
    } = useWizardStore();

    const set = (data) => !readOnly && updateStep2(data);

    return (
        <div className="space-y-6" data-testid="step2-greenfield">
            <header>
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Step 2 of 7</p>
                <h2 className="mt-2 font-display font-semibold text-3xl tracking-tight">Greenfield options</h2>
                <p className="mt-2 text-[13.5px] text-text-muted max-w-2xl">
                    Pre-deployment factory cleanup. These toggles control how aggressively the generated config purges the vendor's out-of-box defaults. Most fresh deployments should leave the recommended defaults on.
                </p>
            </header>

            {vendor === "fortinet" && (
                <>
                    <ToggleRow
                        icon={Network}
                        title="Destroy hardware switch"
                        description="Extract all hardware-switch member ports so they can be individually assigned to VLANs and LACP aggregates. Required for granular topology."
                        why='FortiGate models with a built-in switch (e.g., the 60F internal1–5) bundle ports into a logical "internal" switch on factory boot. ZeroConf decomposes this so each port becomes independently addressable.'
                        on={destroy_switch}
                        onChange={(v) => set({ destroy_switch: v })}
                        testId="destroy-switch"
                        defaultOn
                        warningWhenOff="VLAN port assignment may behave unexpectedly with the factory switch in place."
                        disabled={readOnly}
                    />
                    <ToggleRow
                        icon={Trash2}
                        title="Purge factory DHCP servers"
                        description="Remove all factory-default DHCP server instances. Required for a clean greenfield deployment."
                        why="FortiOS ships with DHCP server IDs 1–5 pre-configured for the factory internal LAN. They block interface IP changes if left in place."
                        on={remove_dhcp}
                        onChange={(v) => set({ remove_dhcp: v })}
                        testId="remove-dhcp"
                        defaultOn
                        warningWhenOff="Always recommended — leaving factory DHCP enabled causes interface-bind errors during apply."
                        disabled={readOnly}
                    />
                    <ToggleRow
                        icon={ShieldOff}
                        title="Disable FortiLink + virtual-switch"
                        description="Remove the fortilink interface and virtual-switch construct. Disable only if you plan to deploy FortiSwitches downstream."
                        why="The fortilink interface and virtual-switch are pre-wired for FortiSwitch integration. They occupy physical ports that ZeroConf needs to reassign."
                        on={disable_fortilink}
                        onChange={(v) => set({ disable_fortilink: v })}
                        testId="disable-fortilink"
                        defaultOn
                        warningWhenOff="If you keep this ON without FortiSwitches downstream, ZeroConf will free those ports for VLAN/LACP use."
                        disabled={readOnly}
                    />
                </>
            )}

            {vendor === "watchguard" && (
                <ToggleRow
                    icon={Trash2}
                    title="Purge factory IPs & DHCP on eth1+"
                    description="WatchGuard Fireboxes ship with 10.0.x.1/24 + a DHCP server on every internal port. ZeroConf clears every factory IP/DHCP before applying the real topology."
                    why="Without this, applying a new IP to eth1 fails because the factory DHCP server is still bound to the previous IP."
                    on={remove_dhcp}
                    onChange={(v) => set({ remove_dhcp: v })}
                    testId="remove-dhcp"
                    defaultOn
                    warningWhenOff="Always recommended — apply will fail otherwise."
                    disabled={readOnly}
                />
            )}

            {(vendor === "zyxel" || vendor === "zyxel_uos") && (
                <ToggleRow
                    icon={Network}
                    title="Destroy LAN1/LAN2 default bridge + purge LAN_POOL DHCP"
                    description="Remove ports from the default `lan1` / `lan2` bridges and disable the LAN1_POOL / LAN2_POOL DHCP servers."
                    why="Zyxel ZLD/uOS pre-assigns geX ports to the lan1/lan2 bridge zones with bound DHCP pools. ZeroConf separates them so each port can be individually mapped to a VLAN."
                    on={destroy_switch || remove_dhcp}
                    onChange={(v) => set({ destroy_switch: v, remove_dhcp: v })}
                    testId="zyxel-cleanup"
                    defaultOn
                    warningWhenOff="Almost always required for a clean greenfield."
                    disabled={readOnly}
                />
            )}

            {vendor === "pfsense" && (
                <ToggleRow
                    icon={Trash2}
                    title="Purge factory configuration"
                    description="pfSense ships with em0=WAN/em1=LAN + a DHCP server on em1. ZeroConf overrides the entire config.xml so this happens implicitly — toggle off only if you are merging into an existing config."
                    why="pfSense XML imports are wholesale replacements. There's no partial-merge mode in the GUI restore."
                    on={remove_dhcp}
                    onChange={(v) => set({ remove_dhcp: v })}
                    testId="remove-dhcp"
                    defaultOn
                    warningWhenOff="Disable only if you know what you're doing — manual XML splicing required."
                    disabled={readOnly}
                />
            )}

            {!vendor && (
                <div className="rounded-lg border border-white/[0.06] bg-surface/40 p-6 text-center text-text-muted text-[13px]">
                    Select a vendor in Step 1 first.
                </div>
            )}

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="rounded-md border border-white/[0.04] bg-canvas/40 p-3 text-[11px] font-mono text-text-dim"
            >
                <span className="text-accent-cyan">i</span> The right panel updates live — toggle a switch and watch the generated CLI/XML change.
            </motion.div>
        </div>
    );
};

export function isStep2Valid(_state) {
    // Greenfield toggles are always valid.
    return true;
}

export default Step2Greenfield;
